import { z } from "zod";

/**
 * Lançamento de conta a receber com N parcelas (paridade com o desktop).
 *
 * `valorParcela` é o valor de UMA parcela — o título vale
 * `valorParcela × qtdParcelas`. O desktop trabalha assim (`TxtValorOriginal ×
 * msk_qtd_parcelas`), e é o que evita a divisão do total e a consequente sobra
 * de centavos a distribuir entre as parcelas.
 */
export const contaReceberSchema = z.object({
  clienteId: z.coerce
    .number({ invalid_type_error: "ID do cliente deve ser um número" })
    .int()
    .min(1, "ID do cliente é obrigatório"),
  dtEmissao: z.string().min(1, "Data de emissão é obrigatória"),
  /** Vencimento da 1ª parcela; as demais saem de `diasParcelas`. */
  dtVencimento: z.string().min(1, "Data de vencimento é obrigatória"),
  valorParcela: z.coerce
    .number({ invalid_type_error: "Valor deve ser um número" })
    .positive("Valor deve ser maior que zero")
    .max(999999999.99, "Valor muito alto"),
  // 50 = tamanho da coluna no banco (s_numdocumento). Antes limitado a 20, o que
  // bloqueava a EDIÇÃO de um título cujo documento já tinha mais de 20 caracteres.
  numDocumento: z.string()
    .trim()
    .max(50, "Número do documento deve ter no máximo 50 caracteres")
    .optional(),
  qtdParcelas: z.coerce.number().int().min(1, "Mínimo 1 parcela").max(360, "Máximo 360 parcelas").default(1),
  /** Deslocamentos em dias a partir do 1º vencimento: "7,14,28". Vazio → 30 em 30. */
  diasParcelas: z.string()
    .trim()
    .regex(/^(\s*\d+\s*)(,\s*\d+\s*)*$/, "Use números separados por vírgula (ex.: 7,14,28)")
    .optional()
    .or(z.literal("")),
  /** Mantém o dia do mês do 1º vencimento nas demais parcelas. */
  diaFixo: z.boolean().default(false),
  lancamento: z.string().trim().max(200, "Descrição muito longa").optional(),
  // Classificação contábil — editável inclusive depois de recebimentos (é
  // metadado, não mexe no parcelamento). 0/undefined = não classificado.
  contaContabilId: z.coerce.number().int().nonnegative().optional(),
  tipoDespesasId: z.coerce.number().int().nonnegative().optional(),
});

export type ContaReceberFormData = z.infer<typeof contaReceberSchema>;

/**
 * Espelha `CronogramaParcelas.Gerar` do backend para PRÉ-VISUALIZAR os
 * vencimentos enquanto o usuário digita. A verdade continua sendo o servidor —
 * isto é só para o usuário conferir o carnê antes de gravar.
 */
export function preverVencimentos(
  primeiroVencimento: string,
  qtdParcelas: number,
  diasParcelas?: string,
  diaFixo = false,
): Date[] {
  if (!primeiroVencimento) return [];

  // `new Date("2026-01-10")` é interpretado como UTC e volta um dia no
  // fuso do Brasil; montar por componentes mantém a data local.
  const [ano, mes, dia] = primeiroVencimento.split("-").map(Number);
  if (!ano || !mes || !dia) return [];

  const primeiro = new Date(ano, mes - 1, dia);
  const qtd = Math.min(Math.max(1, qtdParcelas || 1), 360);

  const lista = (diasParcelas ?? "")
    .split(",")
    .map((p) => parseInt(p.trim(), 10))
    .filter((n) => Number.isFinite(n));

  const ultimo = lista.length > 0 ? lista[lista.length - 1] : 0;
  const intervalo = ultimo > 0 ? ultimo : 30;

  const somarDias = (d: Date, n: number) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

  const datas = [primeiro];
  let cursor = primeiro;

  for (let i = 1; i < qtd; i++) {
    cursor = lista.length >= i
      ? somarDias(primeiro, lista[i - 1])
      : somarDias(cursor, intervalo);

    if (diaFixo) {
      const ultimoDiaDoMes = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
      datas.push(new Date(cursor.getFullYear(), cursor.getMonth(),
        Math.min(primeiro.getDate(), ultimoDiaDoMes)));
    } else {
      datas.push(cursor);
    }
  }

  return datas;
}
